import {Actions, openDialog, Separator} from 'platform/components';
import {Heading, HStack, Show, VStack, Space} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';

import {isNil, isNotNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {metadaApi} from '@dms/api/base';
import {useLazyGetCustomerV2Query} from '@dms/api/customer';
import {
  GetDirectSaleResponse,
  GetWarehousesResponse,
  useBulkDeleteDirectSaleItemsMutation,
  useBulkReturnDirectSaleSparePartsMutation,
  useLazyGetDirectSaleQuery,
  usePatchDirectSaleGetCurrentPriceMutation,
  usePatchPartsRequestCancelByOriginEntityMutation,
  usePostIssueNotesMutation,
  usePostPartsRequestMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  DmsDataGrid,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  getNaturalPersonFullName,
  handleApiError,
  usePermissions,
} from '@dms/shared';
import {WAREHOUSE_DIALOG_IDS} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {Nullish, RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {BulkAssignMechanic} from './BulkAssignMechanic';
import {RequestTabEdit, RequestTabEditProps} from './RequestTabEdit';
import {RequestTabLabourModal} from './RequestTabLabourModal';
import {RequestTabMaterialModal} from './RequestTabMaterialModal';

type DirectSaleItemRowData = {
  id: string;
  type: {value: {key: string; label: string}};
  name: {value: string};
  directSaleId?: {value: string};
  unitPrice?: {value: {amount: number; currency: string}};
  amount?: {value: number};
  state: {value: {key: string; label: string}};
  neededAt?: {value: string};
  articleId?: {value: string};
  warehouseId?: {value: string};
};

interface WorkItemsSegmentProps extends RequiredTestIdProps {
  directSale: GetDirectSaleResponse | Nullish;
  isEditingDisabled: boolean;
  availableWarehouses: GetWarehousesResponse;
  onMaterialReturnModalOpen: VoidFunction;
}

export function WorkItemsSegment(props: WorkItemsSegmentProps) {
  const {directSaleId} = useWarehouseParams();

  const [directSaleEdit] = usePermissions({
    permissionKeys: ['directSaleEdit'],
  });

  const dispatch = useDispatch();

  const [isLabourModalVisible, openLabourModal, closeLabourModal] = useBoolean();
  const [isMaterialModalVisible, openMaterialModal, closeMaterialModal] = useBoolean();

  const [editDialogProps, setEditDialogProps] = useState<RequestTabEditProps | null>(null);

  const [getDirectSaleDetail] = useLazyGetDirectSaleQuery();
  const [getCustomer] = useLazyGetCustomerV2Query();
  const [createIssueNote] = usePostIssueNotesMutation();
  const [postPartsRequest] = usePostPartsRequestMutation();
  const [deleteDirectSalesItems] = useBulkDeleteDirectSaleItemsMutation();
  const [patchDirectSaleGetCurrentPrice] = usePatchDirectSaleGetCurrentPriceMutation();
  const [cancelPartsRequest] = usePatchPartsRequestCancelByOriginEntityMutation();
  const [bulkReturnSpareParts] = useBulkReturnDirectSaleSparePartsMutation();

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {directSaleId}]),
    [directSaleId]
  );

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = async ({
    actionKey,
    rowId,
    rowData,
    refreshData,
    queryId,
    deselectAll,
  }) => {
    const id = isArray(rowId) ? rowId[0] : rowId;
    const directSaleItems = (isArray(rowData) ? rowData : [rowData]) as DirectSaleItemRowData[];

    await match(actionKey)
      .with('getCurrentPrices', async () => {
        await patchDirectSaleGetCurrentPrice({
          directSaleId,
          body: {directSaleItemId: rowId as string[]},
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('redirectDetail', 'edit', async () => {
        const directSaleDetail = getDirectSaleDetail({directSaleId}).unwrap().catch(handleApiError);

        await directSaleDetail.then((data) => {
          const isEditingDisabled = data?.state === 'LOCKED' || data?.state === 'CLOSED';

          const handleClose = () => {
            setEditDialogProps(null);
            refreshData();
            // Invalidating manually onClose to keep dialog rowControl saving feature intact.
            dispatch(metadaApi.util.invalidateTags(['directSales']));
          };

          setEditDialogProps({
            itemId: id,
            isEditingDisabled,
            dataQueryId: queryId,
            directSaleId,
            authorizationProfileId: data?.authorizationProfileId,
            onClose: handleClose,
            'data-testid': props['data-testid'] as string,
          });
        });
      })
      .with('delete', async () => {
        const itemsIds = directSaleItems.map((directSaleItem) => ({id: directSaleItem.id}));

        await deleteDirectSalesItems({
          directSaleId,
          body: {
            items: itemsIds,
          },
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('issue', async () => {
        const directSaleNumber = props.directSale?.number;
        const directSaleAuthorizationProfileId = props.directSale?.authorizationProfileId;

        if (isNil(directSaleNumber)) {
          throw new Error('directSaleNumber is not defined');
        }

        if (isNil(directSaleAuthorizationProfileId)) {
          throw new Error('directSaleAuthorizationProfileId is not defined');
        }

        const customerDataPromise = isNotNil(props.directSale?.customerId)
          ? getCustomer({customerId: props.directSale?.customerId ?? ''})
              .unwrap()
              .catch(handleApiError)
          : Promise.resolve(null);
        await customerDataPromise.then(async (customerData) => {
          await createIssueNote({
            directSaleId,
            body: {
              originDocId: directSaleId,
              originDocType: 'direct-sale',
              originDocNumber: directSaleNumber,
              authorizationProfileId: directSaleAuthorizationProfileId,
              customerName: getNaturalPersonFullName(customerData?.foundingPerson) ?? undefined,
              items: directSaleItems.map((directSaleItem) => ({
                warehouseId: directSaleItem.warehouseId?.value ?? '',
                originItemId: directSaleItem.id,
                articleId: directSaleItem.articleId?.value as string,
                requestItemOriginEntityType: 'wrh_sale_item',
              })),
            },
          })
            .unwrap()
            .then(() => refreshData())
            .catch(handleApiError);
        });
      })
      .with('requestCancellation', async () => {
        const originEntityIds = directSaleItems.map((directSaleItem) => directSaleItem.id);

        await cancelPartsRequest({
          directSaleId,
          body: {
            originEntityId: originEntityIds,
          },
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('request', async () => {
        await postPartsRequest({
          directSaleId,
          body: {
            originEntityType: 'wrh_sale_item',
            requestItems: directSaleItems.map((directSaleItem) => ({
              originEntityId: directSaleItem.id,
              articleId: directSaleItem.articleId?.value as string,
              originEntityHeaderType: 'direct-sale',
              originEntityHeaderId: directSaleId,
              itemNeededAtDate: directSaleItem?.neededAt?.value,
              proposedSalePrice: directSaleItem.unitPrice?.value?.amount,
              quantity: directSaleItem.amount?.value ?? 0,
            })),
          },
        })
          .unwrap()
          .then(() => refreshData())
          .then(deselectAll)
          .catch(handleApiError);
      })

      .with('return', async () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        await bulkReturnSpareParts({directSaleId, body: {directSaleItemId: ids}})
          .unwrap()
          .then(deselectAll)
          .then(props.onMaterialReturnModalOpen)
          .catch(handleApiError);
      })
      .with('assignMechanic', () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        const authorizationProfileId = props.directSale?.authorizationProfileId;

        if (isNil(authorizationProfileId)) {
          throw new Error('Cannot assign mechanic. The authorization profile id is missing.');
        }

        openDialog(
          <BulkAssignMechanic
            directSaleId={directSaleId}
            authorizationProfileId={authorizationProfileId}
            rowIds={ids}
            onSuccess={() => {
              refreshData();
              deselectAll();
            }}
            data-testid={suffixTestId('dialogs.assignMechanic', props)}
          />,
          {
            id: WAREHOUSE_DIALOG_IDS.bulkAssignMechanicDialog,
            title: i18n.t('entity.warehouse.labels.assignMechanic'),
            size: 'small',
            scrollBehavior: 'outside',
          }
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <VStack height="100%">
      <RequestTabEdit {...(editDialogProps as RequestTabEditProps)} />

      <Separator />

      <HStack justify="space-between">
        <Heading size={4}>{i18n.t('entity.warehouse.labels.workItemsHeading')}</Heading>
        <Actions
          actions={[
            {
              type: 'button',
              title: i18n.t('general.actions.addWork'),
              variant: 'ghostLink',
              leftIcon: 'content/add_circle',
              onClick: openLabourModal,
              isDisabled: props.isEditingDisabled || !directSaleEdit,
              tooltip: !directSaleEdit
                ? i18n.t('module.warehouse.label.missingDirectSaleEditPermission')
                : undefined,
              'data-testid': suffixTestId('requestAction.addLabour', props),
            },
            {
              type: 'button',
              title: i18n.t('general.actions.addMaterial'),
              variant: 'ghostLink',
              leftIcon: 'content/add_circle',
              onClick: openMaterialModal,
              isDisabled: props.isEditingDisabled || !directSaleEdit,
              tooltip: !directSaleEdit
                ? i18n.t('module.warehouse.label.missingDirectSaleEditPermission')
                : undefined,
              'data-testid': suffixTestId('requestAction.addMaterial', props),
            },
          ]}
          data-testid={suffixTestId('listActions', props)}
        />
      </HStack>

      <Space vertical={4} />

      <DmsDataGrid
        gridCode={WAREHOUSE_GRID_CODES.warehouseDirectSaleItem}
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        queryModifier={queryModifier}
        emptyState={{
          headline: i18n.t('entity.warehouse.notifications.noWorkItems'),
          subheadline: i18n.t('entity.warehouse.notifications.addWorkItems'),
        }}
        data-testid="requestedItems"
      />

      <Show when={isLabourModalVisible}>
        <RequestTabLabourModal
          directSaleId={directSaleId}
          authorizationProfileId={props.directSale?.authorizationProfileId}
          onDiscard={closeLabourModal}
          data-testid={testIds.warehouse.directSalesDetailOverview('dialogs.addLabour')}
        />
      </Show>

      <Show when={isMaterialModalVisible}>
        <RequestTabMaterialModal
          directSaleId={directSaleId}
          availableWarehouses={props.availableWarehouses}
          authorizationProfileId={props.directSale?.authorizationProfileId}
          onDiscard={closeMaterialModal}
          data-testid={testIds.warehouse.directSalesDetailOverview('dialogs.addMaterial')}
        />
      </Show>
    </VStack>
  );
}

const PREDEFINED_COLUMNS = ['articleId', 'warehouseId'] as const;
